import { useState } from 'react';
import type { UniqueIdentifier } from '@dnd-kit/core';
import type { ProposalExtraItemType } from '@econtractor/types';
import { safeFloatParse } from 'utils/helpers/safeParsing';
import { buildSalePatch } from '../../model/templatePriceUtils';
import { getSortableRowId, reorderItemsById } from '../../model/sortableTableRows';

export interface UseAdditionalItemsTableParams {
    items: ProposalExtraItemType[];
    rate: number;
    onChange: (items: ProposalExtraItemType[]) => void;
}

const additionalRowId = (row: ProposalExtraItemType, index: number) =>
    getSortableRowId(row, `${row.itemName}-${index}`);

const useAdditionalItemsTable = ({ items, rate, onChange }: UseAdditionalItemsTableParams) => {
    const [editNameIndex, setEditNameIndex] = useState<number | null>(null);
    const [editPriceIndex, setEditPriceIndex] = useState<number | null>(null);
    const [editCountIndex, setEditCountIndex] = useState<number | null>(null);

    const removeRow = (targetIndex: number) => {
        onChange(items.filter((_, index) => index !== targetIndex));
    };

    const updateRow = (targetIndex: number, patch: Partial<ProposalExtraItemType>) => {
        onChange(items.map((row, index) => (index === targetIndex ? { ...row, ...patch } : row)));
    };

    const reorderByIds = (activeId: UniqueIdentifier, overId: UniqueIdentifier) => {
        onChange(reorderItemsById(items, activeId, overId, additionalRowId));
    };

    const clearInlineEdits = () => {
        setEditNameIndex(null);
        setEditPriceIndex(null);
        setEditCountIndex(null);
    };

    const openNameEdit = (index: number) => {
        setEditPriceIndex(null);
        setEditCountIndex(null);
        setEditNameIndex(index);
    };

    const openPriceEdit = (index: number) => {
        setEditNameIndex(null);
        setEditCountIndex(null);
        setEditPriceIndex(index);
    };

    const savePriceEdit = (index: number, value: string | number) => {
        const row = items[index];
        const patch = buildSalePatch(safeFloatParse(value || 0) || 0, rate);
        // Legacy: price тримав sale — при першому edit обнуляємо cost, sale йде в priceSale
        const isLegacy = row?.priceSale == null && row?.priceSaleRate == null;
        updateRow(index, isLegacy ? { ...patch, price: 0, priceRate: 0 } : patch);
        setEditPriceIndex(null);
    };

    const openCountEdit = (index: number) => {
        setEditNameIndex(null);
        setEditPriceIndex(null);
        setEditCountIndex(index);
    };

    const saveCountEdit = (index: number, value: string | number) => {
        const nextCount = safeFloatParse(value || 0) || 0;
        if (nextCount <= 0) return false;
        updateRow(index, { itemCount: nextCount });
        setEditCountIndex(null);
    };

    const setAsOtherExpense = (targetIndex: number, checked: boolean) => {
        onChange(
            items.map((row, index) => {
                if (index !== targetIndex) return row;
                if (checked) return { ...row, asOtherExpense: true };
                const { asOtherExpense: _omit, ...rest } = row;
                return rest;
            }),
        );
    };

    return {
        editNameIndex,
        setEditNameIndex,
        editPriceIndex,
        setEditPriceIndex,
        editCountIndex,
        setEditCountIndex,
        openNameEdit,
        openPriceEdit,
        savePriceEdit,
        openCountEdit,
        saveCountEdit,
        setAsOtherExpense,
        updateRow,
        removeRow,
        reorderByIds,
        clearInlineEdits,
        getRowId: additionalRowId,
    };
};

export default useAdditionalItemsTable;
